import React from 'react';

import classNames from 'classnames';

import { box, CONTENT_WIDTH, lineHeight, MARGIN_X, MATRIX, pt } from '../slideGeometry';

const CELL_CLASSES = {
    R: 'tw-bg-red tw-text-white',
    A: 'tw-bg-amber-400 tw-text-charcoal',
    G: 'tw-bg-green-25 tw-text-charcoal',
};

const LABEL_SIZE = 7;

const labelStyle = (size) => ({ fontSize: pt(size ?? LABEL_SIZE) });

const Grid = ({ grid, x, y, labelSizes = [], footnoteSize }) => {
    const cellsX = x + MATRIX.LABEL_WIDTH;
    const cellWidth = Math.floor((MATRIX.GRID_WIDTH - MATRIX.LABEL_WIDTH) / 4);
    const top = y + MATRIX.TITLE_HEIGHT;
    const labelsY = top + 4 * MATRIX.CELL_ROW_HEIGHT;

    return (
        <>
            <div className="tw-text-charcoal-60 tw-text-end" style={{ ...box(x, y, MATRIX.LABEL_WIDTH - 4, MATRIX.TITLE_HEIGHT), ...labelStyle() }}>
                {$t('Impact')}
            </div>
            <div
                className="tw-text-charcoal tw-font-bold"
                style={{ ...box(cellsX, y, MATRIX.GRID_WIDTH - MATRIX.LABEL_WIDTH, MATRIX.TITLE_HEIGHT), fontSize: pt(8) }}
            >
                {grid.title}
            </div>
            {(grid.rows || []).map((row, rowIndex) => {
                const rowY = top + rowIndex * MATRIX.CELL_ROW_HEIGHT;

                return (
                    <React.Fragment key={row.label}>
                        <div
                            className="tw-text-charcoal-60 tw-text-end"
                            style={{ ...box(x, rowY + 1, MATRIX.LABEL_WIDTH - 4, MATRIX.CELL_ROW_HEIGHT), ...labelStyle(labelSizes[rowIndex]) }}
                        >
                            {row.label}
                        </div>
                        {(row.cells || []).map((cell, cellIndex) => (
                            <div
                                key={cellIndex}
                                className={classNames(
                                    'tw-flex tw-items-center tw-justify-center tw-font-bold',
                                    CELL_CLASSES[cell.rating] || 'tw-bg-charcoal-10 tw-text-charcoal',
                                )}
                                style={{
                                    ...box(cellsX + cellIndex * cellWidth, rowY, cellWidth - MATRIX.CELL_GAP, MATRIX.CELL_ROW_HEIGHT - MATRIX.CELL_GAP),
                                    fontSize: pt(8),
                                }}
                            >
                                {cell.count > 0 ? cell.count : ''}
                            </div>
                        ))}
                    </React.Fragment>
                );
            })}
            <div
                className="tw-text-charcoal-60 tw-text-end"
                style={{ ...box(x, labelsY + 1, MATRIX.LABEL_WIDTH - 4, MATRIX.COLUMN_LABEL_HEIGHT), ...labelStyle() }}
            >
                {$t('Likelihood')}
            </div>
            {(grid.columns || []).map((label, index) => (
                <div
                    key={label}
                    className="tw-text-charcoal-60 tw-text-center"
                    style={{ ...box(cellsX + index * cellWidth, labelsY + 1, cellWidth - MATRIX.CELL_GAP, MATRIX.COLUMN_LABEL_HEIGHT), ...labelStyle() }}
                >
                    {label}
                </div>
            ))}
            {grid.footnote && (
                <div
                    className="tw-italic tw-text-charcoal-60"
                    style={{
                        ...box(cellsX, labelsY + MATRIX.COLUMN_LABEL_HEIGHT, MATRIX.GRID_WIDTH - MATRIX.LABEL_WIDTH, MATRIX.FOOTNOTE_HEIGHT),
                        ...labelStyle(footnoteSize),
                    }}
                >
                    {grid.footnote}
                </div>
            )}
        </>
    );
};

/**
 * The small table beside the matrices (the movement counts), or its message
 * when there is nothing to compare with.
 */
const Side = ({ side, x, y, fit }) => {
    const top = y + MATRIX.TITLE_HEIGHT + 2;
    const noteX = x + MATRIX.SIDE_LABEL_WIDTH + MATRIX.SIDE_VALUE_WIDTH + MATRIX.SIDE_NOTE_GAP;
    const noteWidth = MATRIX.SIDE_WIDTH - MATRIX.SIDE_LABEL_WIDTH - MATRIX.SIDE_VALUE_WIDTH - MATRIX.SIDE_NOTE_GAP;

    return (
        <>
            <div
                className="tw-text-charcoal tw-font-bold"
                style={{ ...box(x, y, MATRIX.SIDE_WIDTH, MATRIX.TITLE_HEIGHT), fontSize: pt(fit.sideCaptionSize ?? 8) }}
            >
                {side.caption}
            </div>
            {side.message ? (
                <div className="tw-italic tw-text-charcoal-60" style={{ ...box(x, top, MATRIX.SIDE_WIDTH, MATRIX.SIDE_MESSAGE_HEIGHT), fontSize: pt(8) }}>
                    {side.message}
                </div>
            ) : (
                (side.rows || []).map((row, index) => {
                    const rowY = top + index * MATRIX.SIDE_ROW_HEIGHT;

                    return (
                        <React.Fragment key={row.label}>
                            <div className="tw-border-b tw-border-charcoal-15" style={box(x, rowY, MATRIX.SIDE_WIDTH, MATRIX.SIDE_ROW_HEIGHT)} />
                            <div
                                className="tw-text-charcoal"
                                style={{
                                    ...box(x, rowY + 1, MATRIX.SIDE_LABEL_WIDTH, MATRIX.SIDE_ROW_HEIGHT - 1),
                                    fontSize: pt(fit.sideLabelSizes?.[index] ?? 8),
                                }}
                            >
                                {row.label}
                            </div>
                            <div
                                className="tw-text-charcoal tw-font-bold tw-text-end"
                                style={{ ...box(x + MATRIX.SIDE_LABEL_WIDTH, rowY + 1, MATRIX.SIDE_VALUE_WIDTH, MATRIX.SIDE_ROW_HEIGHT - 1), fontSize: pt(8) }}
                            >
                                {row.value}
                            </div>
                            {row.note && (
                                <div
                                    className="tw-text-charcoal-60"
                                    style={{ ...box(noteX, rowY + 2, noteWidth, MATRIX.SIDE_ROW_HEIGHT - 2), ...labelStyle(fit.sideNoteSizes?.[index]) }}
                                >
                                    {row.note}
                                </div>
                            )}
                        </React.Fragment>
                    );
                })
            )}
        </>
    );
};

/**
 * The legend's top in the side column: under the side's rows or message, or
 * at the top of the column's body without a side, as MatrixRenderer does.
 */
const legendTop = (side, y) => {
    const top = y + MATRIX.TITLE_HEIGHT + 2;

    if (!side) {
        return top;
    }

    return top + (side.message ? MATRIX.SIDE_MESSAGE_HEIGHT : (side.rows || []).length * MATRIX.SIDE_ROW_HEIGHT);
};

/**
 * Assessment matrices side by side, cells filled by their rating with the
 * count of risks in each, and a column at the end holding the movement counts
 * with the legend under them. The geometry matches the .pptx MatrixRenderer,
 * and each text is drawn at the size the Paginator fitted it to (`fit`).
 */
const MatrixBlock = ({ block }) => {
    const grids = block.grids || [];
    const sideX = MARGIN_X + CONTENT_WIDTH - MATRIX.SIDE_WIDTH;
    const fit = block.fit || {};
    const legendSize = fit.legendSize ?? LABEL_SIZE;

    return (
        <>
            {grids.map((grid, index) => (
                <Grid
                    key={grid.title}
                    grid={grid}
                    x={MARGIN_X + index * (MATRIX.GRID_WIDTH + MATRIX.GRID_GAP)}
                    y={block.y}
                    labelSizes={fit.rowLabelSizes?.[index]}
                    footnoteSize={fit.footnoteSizes?.[index]}
                />
            ))}
            {block.side && <Side side={block.side} x={sideX} y={block.y} fit={fit} />}
            {block.legend && (
                <div
                    className="tw-text-charcoal-60"
                    style={{
                        ...box(
                            sideX,
                            legendTop(block.side, block.y),
                            MATRIX.SIDE_WIDTH,
                            Math.max(MATRIX.LEGEND_HEIGHT, (fit.legendLines ?? 2) * lineHeight(legendSize)),
                        ),
                        ...labelStyle(legendSize),
                    }}
                >
                    {block.legend}
                </div>
            )}
        </>
    );
};

export default MatrixBlock;
